import { useState, useContext } from 'react';
import { TrashIcon, MinusCircleIcon, PlusCircleIcon, ChevronDownIcon, ChevronUpIcon, PencilSquareIcon } from '@heroicons/react/24/outline';
import CartContext from "../../Context/Cart/CartContext";
import { formatCurrency } from '../../Utils';
import { useBrand } from '../../Context/Brand/BrandContext';

export default function OrderDetails({
  toggleShowPayment,
  showPayment,
  onEditItem,
  actionLabel,
  actionPending = false,
  actionDisabled = false,
  actionHint = '',
}) {
  const { removeFromCart, increase, decrease, cartItems, itemCount, total } = useContext(CartContext);
  const { brand } = useBrand();
  const accentColor = brand.primaryColor || '#1A7F6D';
  const [expandedItems, setExpandedItems] = useState({});

  const toggleExpand = (id) => {
    setExpandedItems(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const itemName = (product) => product.item_name || product.name || '';
  const itemImage = (product) => product.image_url || product.image || product.item_image || '';

  return (
    <div>
      <div>
        <div className="mb-4">
          <h2 className="text-lg font-semibold text-gray-900">Items</h2>
        </div>

        <div className="space-y-3">
          {cartItems?.map((product) => {
            const isExpanded = expandedItems[product.item_public_id];
            const options = product.selectedOptions || [];
            return (
              <div key={product.item_public_id} className="bg-gray-50 rounded-xl overflow-hidden border border-gray-100">
                <button
                  onClick={() => toggleExpand(product.item_public_id)}
                  className="w-full flex items-center gap-3 p-3 text-left"
                >
                  <div className="w-14 h-14 rounded-lg overflow-hidden flex-shrink-0 bg-gray-100">
                    {itemImage(product) ? (
                      <img src={itemImage(product)} alt={itemName(product)} className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-gray-300">
                        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                      </div>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2">
                      <h3 className="text-sm font-medium text-gray-900 truncate">{itemName(product)}</h3>
                      <span className="text-xs font-semibold whitespace-nowrap" style={{ color: accentColor }}>{formatCurrency(product.price)}</span>
                    </div>
                    <div className="flex items-center gap-2 mt-1">
                      <span className="text-xs text-gray-400 bg-white rounded-full px-2 py-0.5 border border-gray-100">
                        x{product.quantity}
                      </span>
                      {options.length > 0 && !isExpanded && (
                        <span className="text-xs text-gray-400 truncate">
                          {options.map(o => o.optionName).filter(Boolean).join(', ')}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-medium text-gray-500 whitespace-nowrap">{formatCurrency(product.price * product.quantity)}</span>
                    {isExpanded ? (
                      <ChevronUpIcon className="w-4 h-4 text-gray-400 flex-shrink-0" />
                    ) : (
                      <ChevronDownIcon className="w-4 h-4 text-gray-400 flex-shrink-0" />
                    )}
                  </div>
                </button>

                {isExpanded && (
                  <div className="px-3 pb-3 pt-0 border-t border-gray-100">
                    {options.length > 0 && (
                      <div className="mt-3 space-y-1">
                        {options.map((opt, i) => (
                          <span key={i} className="inline-flex items-center gap-1 text-xs text-gray-500 bg-white rounded-full px-2.5 py-1 mr-1.5 border border-gray-100 shadow-sm">
                            {opt.groupName}: {opt.optionName}
                          </span>
                        ))}
                      </div>
                    )}

                    {product.specialInstructions && (
                      <p className="text-xs text-gray-400 italic mt-2">
                        Note: {product.specialInstructions}
                      </p>
                    )}

                    <div className="flex items-center justify-between mt-3 pt-2 border-t border-gray-100">
                      <div className="flex items-center gap-2 bg-white rounded-full px-2 py-1 border border-gray-100">
                        <button
                          onClick={() => decrease(product)}
                          disabled={product.quantity <= 1}
                          className={`p-0.5 rounded-full ${product.quantity <= 1 ? 'text-gray-300' : 'text-gray-600 hover:bg-gray-100'}`}
                        >
                          <MinusCircleIcon className="w-4 h-4" />
                        </button>
                        <span className="text-gray-900 font-medium min-w-[18px] text-center text-sm">
                          {product.quantity}
                        </span>
                        <button
                          onClick={() => increase(product)}
                          className="p-0.5 rounded-full text-gray-600 hover:bg-gray-100"
                        >
                          <PlusCircleIcon className="w-4 h-4" />
                        </button>
                      </div>
                      <div className="flex items-center gap-1">
                        {onEditItem && (
                          <button
                            onClick={() => onEditItem(product)}
                            className="p-1.5 text-blue-400 hover:text-blue-600 hover:bg-blue-50 rounded-full transition-colors"
                          >
                            <PencilSquareIcon className="w-4 h-4" />
                          </button>
                        )}
                        <button
                          onClick={() => removeFromCart(product)}
                          className="p-1.5 text-red-400 hover:text-red-600 hover:bg-red-50 rounded-full transition-colors"
                        >
                          <TrashIcon className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {(itemCount || 0) > 0 && (
          <div className="mt-3 px-1 flex justify-end">
            <span className="text-xs text-gray-400">{itemCount} item{itemCount !== 1 ? 's' : ''}</span>
          </div>
        )}

        <div className="mt-4 space-y-4">
          <div className="rounded-xl p-4 space-y-2" style={{ backgroundColor: `${accentColor}10` }}>
            <div className="flex justify-between text-sm text-gray-600">
              <span>Subtotal</span>
              <span>{formatCurrency(total)}</span>
            </div>
            <div className="pt-2 border-t flex justify-between font-semibold text-gray-900" style={{ borderColor: `${accentColor}25` }}>
              <span>Total</span>
              <span>{formatCurrency(total)}</span>
            </div>
          </div>

          <button
            onClick={toggleShowPayment}
            disabled={showPayment || actionPending || actionDisabled}
            className="w-full py-4 px-6 text-white rounded-xl font-semibold transition-colors disabled:opacity-50 active:scale-[0.99]"
            style={{ backgroundColor: accentColor }}
          >
            {showPayment || actionPending ? (
              actionPending ? 'Sending items...' : 'Processing...'
            ) : (
              <>{actionLabel || 'Checkout'} · {formatCurrency(total)}</>
            )}
          </button>
          {actionHint && (
            <p className="px-2 text-center text-xs leading-5 text-gray-500">{actionHint}</p>
          )}
        </div>
      </div>
    </div>
  );
}
